<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/stores/user'
import { useLang } from '@/composables/useLang'

const { t, locale } = useI18n()
const userStore = useUserStore()
const { availableLanguages, setLocale: setLocaleFromComposable } = useLang()
const mobileMenuOpen = ref(false)
const langDropdownOpen = ref(false)

const navLinks = [
  { to: '/dashboard', label: t('nav.dashboard') },
  { to: '/visa', label: t('nav.visa') },
  { to: '/cost', label: t('nav.cost') },
  { to: '/timezone', label: t('nav.timezone') },
  { to: '/tax', label: t('nav.tax') },
  { to: '/guide', label: t('nav.guide') },
]

const setLocale = (code: string) => {
  setLocaleFromComposable(code)
  langDropdownOpen.value = false
}

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value
}

const closeMobileMenu = () => {
  mobileMenuOpen.value = false
}
</script>

<template>
  <header class="header-glow sticky top-0 z-[var(--z-sticky)] border-b border-[var(--color-border)] bg-[var(--color-surface)]/80 backdrop-blur-md">
    <div class="mx-auto flex h-14 max-w-7xl items-center justify-between px-4">
      <router-link to="/" class="flex items-center gap-2 text-lg font-bold text-[var(--color-primary)]">
        <span>🌍</span>
        <span>{{ t('app.name') }}</span>
      </router-link>

      <nav class="hidden items-center gap-4 md:flex">
        <router-link
          v-for="link in navLinks"
          :key="link.to"
          :to="link.to"
          class="text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] transition-colors"
        >{{ link.label }}</router-link>
      </nav>

      <div class="flex items-center gap-2">
        <div class="relative">
          <button
            @click="langDropdownOpen = !langDropdownOpen"
            class="rounded-md border border-[var(--color-border)] px-2 py-1 text-xs text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)] transition-colors"
          >
            {{ availableLanguages.find(l => l.code === locale)?.nativeName || '中文' }}
          </button>
          <div
            v-if="langDropdownOpen"
            class="absolute right-0 top-full mt-1 w-36 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] py-1 shadow-[var(--shadow-lg)] z-[var(--z-dropdown)]"
          >
            <button
              v-for="lang in availableLanguages"
              :key="lang.code"
              @click="setLocale(lang.code)"
              class="w-full px-3 py-1.5 text-left text-sm hover:bg-[var(--color-surface-hover)] transition-colors"
              :class="locale === lang.code ? 'text-[var(--color-primary)] font-medium' : 'text-[var(--color-text-secondary)]'"
            >{{ lang.nativeName }}</button>
          </div>
        </div>

        <router-link
          v-if="userStore.isLoggedIn"
          to="/settings"
          aria-label="User settings"
          class="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--color-primary-100)] text-sm font-medium text-[var(--color-primary)]"
        >{{ userStore.userInfo?.username?.charAt(0)?.toUpperCase() || 'U' }}</router-link>
        <router-link
          v-else
          to="/login"
          aria-label="Login"
          class="rounded-md bg-[var(--color-primary)] px-3 py-1.5 text-xs font-medium text-white hover:bg-[var(--color-primary-dark)] transition-colors"
        >{{ t('nav.login') }}</router-link>

        <button
          @click="toggleMobileMenu"
          aria-label="Toggle menu"
          class="ml-1 flex h-8 w-8 items-center justify-center rounded-md text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)] md:hidden"
        >
          <svg v-if="!mobileMenuOpen" class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/></svg>
          <svg v-else class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </div>
    </div>

    <div v-if="mobileMenuOpen" class="border-t border-[var(--color-border)] bg-[var(--color-surface)] px-4 pb-4 md:hidden">
      <nav class="flex flex-col gap-2 pt-2">
        <router-link
          v-for="link in navLinks"
          :key="link.to"
          :to="link.to"
          @click="closeMobileMenu"
          class="rounded-md px-3 py-2 text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-primary)] transition-colors"
        >{{ link.label }}</router-link>
      </nav>
    </div>
  </header>
</template>
